import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import api from "../lib/api";
import { toast } from "sonner";
import { SOCIAL_PROOF } from "../config/socialProof";
import { reconcileCheckoutReturn } from "../lib/checkoutBeacon";
import { trackMetaFunnelEvent } from "../lib/metaFunnelEvents";
import {
  beginPlusPurchase,
  consumePlusPurchaseEntry,
  resetPurchaseInFlight,
} from "../lib/beginPlusPurchase";
import TrialBanner from "../components/TrialBanner";
import { hasToken as _hasToken } from "../lib/nativeTokenStorage";
// Iter XX.218 — App Review readiness. Native platform detection so
// the Plus page renders App-Store-appropriate copy on iOS/Android
// and web Stripe copy on the web bundle.
import { isNative, isIOS } from "../lib/nativePlatform";
// Restore Purchases surfaced on the Plus page itself (not only inside
// PaywallModal) so Apple can find it during App Review.
import RestorePurchasesButton from "../components/RestorePurchasesButton";
import NativePurchaseCta from "../components/NativePurchaseCta";
// Iter XX.267 — Gully Pro Pricing Anchor + Waitlist. Non-purchasable
// Pro tier card lives BELOW the existing Free/Plus comparison so the
// current purchase flow, analytics and layout stay 1:1. Never touches
// billing / entitlements / RevenueCat / Stripe.
import ProTierCard from "../components/ProTierCard";
import { trackProEvent } from "../lib/proWaitlist";
// Iter XX.267b — light haptic on the Plus CTA tap.
import { hapticLight } from "../lib/haptics";

/**
 * Gully Plus — Pricing page.
 *
 * Hero: video-first ("Keep improving with unlimited video reviews").
 * Iter 54-revised: pricing is sourced LIVE from Stripe — the
 * `local.display` string returned by `/api/billing/plus/pricing`
 * comes from `stripe.Price.retrieve(price_id).unit_amount + currency`
 * for the user's country bucket, and the SAME Price ID is used at
 * checkout. Display equals charge — always.
 * Funnel-tracks: plus_page_view (on mount), checkout_start (on click),
 * conversion (via webhook on Stripe success).
 */
export default function Upgrade() {
  const [pricing, setPricing] = useState(null);
  const [billing, setBilling] = useState(null);
  const [valuePreview, setValuePreview] = useState(null);
  // Iter XX.168 — Personalized Plus context. Read-only. Null until
  // fetched; if the user is anonymous or has thin history, level=C
  // and the frontend silently falls back to the generic Plus copy.
  const [personalization, setPersonalization] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const cameFromPaywall = params.get("from") === "paywall";

  // Iter XX.20 (P0) — StrictMode-safe single-fire guards for the /plus
  // analytics beacons. Under React 18 dev the top-level effect runs
  // twice; without these refs `plus_payment_page_opened` fires 2×.
  const viewFiredRef = useRef(false);
  const scrollRef = useRef({ s25: false, s50: false, s75: false, pricing: false });

  // Iter XX.20 (P0) — always send `session_id` on /plus/track so
  // anonymous /plus visits stay attributable via web_sessions.
  const gullySessionId = (() => {
    try { return localStorage.getItem("gully_web_session_id") || null; }
    catch { return null; }
  })();

  // Funnel: fire `gully_plus_page_viewed` once per mount
  useEffect(() => {
    // Iter XX.20 (P0) — StrictMode-safe. React 18 dev double-mounts
    // effects; without this guard `plus_payment_page_opened` fires 2×.
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;

    // Iter 54 — canonical event name (legacy `plus_page_view` kept for
    // backward compat in same call).
    api.post("/billing/plus/page-view", {
      source: cameFromPaywall ? "paywall" : "plus_page",
    }).catch(() => {});
    // Iter XX.4 — mirror into plus_analytics_events for funnel/CTR analytics.
    // Iter XX.20 (P0) — attach `session_id` so anon /plus visits are
    // attributable via web_sessions.
    api.post("/plus/track", {
      kind: "plus_payment_page_opened",
      session_id: gullySessionId,
      source: cameFromPaywall ? "paywall" : "plus_page",
    }).catch(() => {});
    // Iter XX.267 — additive `pricing_viewed` beacon so we can measure
    // whether the Free/Plus/Pro anchor lifts Plus CTA & purchase rate.
    // Uses the SAME /plus/track backend — no rename of existing events.
    trackProEvent(
      "pricing_viewed",
      cameFromPaywall ? "paywall" : "plus_page",
      gullySessionId,
    );
    trackProEvent(
      "plus_plan_viewed",
      cameFromPaywall ? "paywall" : "plus_page",
      gullySessionId,
    );
    try {
      api.post("/client-error", {
        kind: "gully_plus_page_viewed",
        session_id: null,
        payload: { source: cameFromPaywall ? "paywall" : "plus_page" },
      }).catch(() => {});
    } catch { /* swallow */ }
    // Iter XX.224 — iOS Meta App Event (viewedContent equivalent).
    // Consent-gated inside the dispatcher; no-op on web / Android.
    try {
      // Lazy import so web bundle can drop-shake if unused.
      import("../lib/iosMetaEvents").then(m => {
        m.logPlusViewed(cameFromPaywall ? "paywall" : "plus_page");
      }).catch(() => {});
    } catch { /* swallow */ }
    // Iter 54 — derive country from the browser locale as a client-side
    // hint so India users see INR even when the ingress strips
    // CF-IPCountry. Backend prioritises explicit `?country=` over any
    // header signal, sanitises to 2 letters, falls back to US for junk.
    // The URL `?country=XX` query param overrides everything else
    // (useful for direct share links and for QA / testing).
    let countryHint = "";
    const urlCountry = (params.get("country") || "").trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(urlCountry)) {
      countryHint = urlCountry;
    }
    try {
      // Prefer the FIRST language preference; e.g. "en-IN" → "IN".
      if (!countryHint) {
        const langs = (navigator.languages && navigator.languages.length)
          ? navigator.languages : [navigator.language || ""];
        for (const l of langs) {
          const m = /[a-z]{2,3}-([A-Z]{2})/.exec(String(l));
          if (m) { countryHint = m[1]; break; }
        }
      }
      // Last resort: Intl timezone heuristic (Asia/Kolkata → IN).
      if (!countryHint && typeof Intl !== "undefined") {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (/Kolkata|Calcutta/.test(tz)) countryHint = "IN";
        else if (/London/.test(tz)) countryHint = "GB";
        else if (/Toronto|Vancouver|Montreal/.test(tz)) countryHint = "CA";
        else if (/Sydney|Melbourne|Perth/.test(tz)) countryHint = "AU";
      }
    } catch { /* swallow */ }
    const url = countryHint
      ? `/billing/plus/pricing?country=${encodeURIComponent(countryHint)}`
      : "/billing/plus/pricing";
    api.get(url).then((r) => setPricing(r.data)).catch(() => {});
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    // Iter XX.17 — Gully Plus Value Packaging. Deterministic, 0-LLM,
    // reads player_memory.journey_snapshot when the caller is signed in.
    api.get("/plus/value-preview").then((r) => setValuePreview(r.data)).catch(() => {});

    // Iter XX.19 — Conversion Intelligence beacons. Fire each scroll
    // threshold and the pricing-section-reached event AT MOST ONCE per
    // page load. Iter XX.20 (P0) — StrictMode-safe: guard lives in a
    // ref outside the effect closure so both mount passes share it.
    // Also carries session_id so anon events are still attributable.
    const fired = scrollRef.current;
    function _post(kind) {
      try {
        api.post("/plus/track", {
          kind,
          session_id: gullySessionId,
          source: cameFromPaywall ? "paywall" : "plus_page",
        }).catch(() => {});
      } catch { /* swallow */ }
    }
    function onScroll() {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      const pct = (doc.scrollTop / total) * 100;
      if (!fired.s25 && pct >= 25) { fired.s25 = true; _post("plus_page_scroll_25"); }
      if (!fired.s50 && pct >= 50) { fired.s50 = true; _post("plus_page_scroll_50"); }
      if (!fired.s75 && pct >= 75) { fired.s75 = true; _post("plus_page_scroll_75"); }
      // Pricing-section reached: sentinel element bottom crosses the viewport top.
      if (!fired.pricing) {
        const el = document.querySelector('[data-testid="plus-comparison"]');
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.75) {
            fired.pricing = true;
            _post("plus_pricing_section_reached");
          }
        }
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also check once on mount (in case content is already visible without scrolling)
    const scrollCheckTimer = setTimeout(onScroll, 500);

    // Iter XX.10 — if the user is coming back from Stripe (successful,
    // cancelled, or just closed the tab), reconcile the checkout-open
    // stash. Fires a `plus_checkout_page_left` beacon with the measured
    // duration when the user did NOT convert. `?cancelled=1` is a hard
    // signal — treat it as a non-conversion regardless of billing state.
    api.get("/billing/me")
      .then((r) => {
        reconcileCheckoutReturn({
          isPlus: !params.get("cancelled") && (r?.data?.plus_status === "active"),
        });
        // Iter XX.282 — Canonical `checkout_cancelled` when the user
        // returned via Stripe's cancel_url (`?cancelled=1`) AND they
        // aren't already Plus. Fires exactly once per return (session-
        // deduped by the milestone_key). First-party only, no Meta
        // CAPI mirror. Preserves all other existing behavior — this
        // is an additive analytics emission.
        try {
          if (params.get("cancelled")
              && r?.data?.plus_status !== "active") {
            const _sid = (typeof localStorage !== "undefined"
                ? localStorage.getItem("gully_web_session_id") : null) || "";
            trackMetaFunnelEvent("checkout_cancelled", {
              milestone_key: `stripe_cancel_return:${_sid}:${Date.now() >> 12}`,
              extra: {
                channel:    "stripe",
                product_id: "plus_monthly_stripe",
                offer_id:   "plus_default_v1",
                source:     "cancel_url_return",
              },
            });
          }
        } catch { /* fire-and-forget */ }
        // Iter XX.72.5 — Entitled-user guard. If Plus is already
        // active OR a valid Pass is in place, DON'T let the user
        // accidentally create a duplicate subscription. Bounce them
        // back to /chat where their entitlement is already in effect.
        // (`cancelled` flag from Stripe return short-circuits this so
        // we don't re-loop the user after a legitimate cancel.)
        if (!params.get("cancelled") && r?.data?.plus_status === "active") {
          // Iter XX.272 — Challenge-origin roundtrip. If the purchase
          // began from Challenge #001, land the user back on the
          // challenge (with joined=1 so ChallengeDetail fires the
          // correlated `challenge_plus_purchase_success` event) —
          // never generic Home. Consumes the stash so subsequent
          // visits don't loop back to the challenge unexpectedly.
          try {
            // eslint-disable-next-line global-require
            const { consumeChallengeOrigin } = require("../lib/challenge");
            const origin = consumeChallengeOrigin();
            if (origin && origin.challenge_id) {
              window.location.href = `/challenge/${origin.challenge_id}?joined=1`;
              return;
            }
          } catch { /* noop — fall through to journey ladder */ }
          // Iter XX.273 — Journey-origin roundtrip (Continue This
          // Journey). Runs SECOND in the ladder so an active
          // Challenge purchase is never masked by a stale journey
          // stash. Routes back to /chat with a marker so Chat.jsx
          // can fire the correlated purchase-success event once.
          try {
            // eslint-disable-next-line global-require
            const { consumeJourneyOrigin } = require("../lib/journeyOrigin");
            const jorigin = consumeJourneyOrigin();
            if (jorigin) {
              window.location.href = "/chat?journey=continue";
              return;
            }
          } catch { /* noop — fall through to /chat */ }
          window.location.href = "/chat";
          return;
        }
      })
      .catch(() => reconcileCheckoutReturn({ isPlus: false }));
    if (params.get("cancelled")) {
      try {
        api.post("/client-error", {
          kind: "gully_plus_checkout_failed",
          session_id: null,
          payload: { reason: "user_cancelled" },
        }).catch(() => {});
        // Iter XX.72.6 — canonical funnel event for cancellation.
        api.post("/web/track", {
          session_id: null,
          event: "checkout_cancelled",
          payload: { reason: "user_cancelled" },
        }).catch(() => {});
      } catch { /* swallow */ }
      toast("Checkout cancelled. No charge made.");
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(scrollCheckTimer);
    };
     
  }, []);

  const local = pricing?.local;
  // Iter 54-revised — render the Stripe-truth price string returned by
  // the backend. The backend lives-fetches the Stripe Price object
  // for this country's bucket — `display` is computed from
  // `unit_amount + currency` on the Stripe side, so it ALWAYS matches
  // what the user will be charged at checkout. If Stripe can't be
  // reached (or the bucket has no Price ID and no global fallback),
  // `display` is null — we render a "loading…" placeholder rather
  // than invent a number.
  const priceDisplay = local?.display || "—";
  const priceCurrency = (local?.currency || "").toUpperCase();
  const pricePeriod = local?.period || "month";
  // Iter XX.267a — `priceLoaded` originally clears ONLY when Stripe's
  // backend response resolves. On native iOS, if the Stripe country
  // bucket has no `display` (unconfigured or unreachable), the button
  // was stuck on "Loading price…" forever even though RevenueCat had
  // already resolved the localized StoreKit `priceString`. We now
  // consider the price loaded when EITHER source has a value. Native
  // callers still prefer the RC value (see `effectivePriceDisplay`).

  // Iter XX.218 — On native (iOS + Android), prefer the StoreKit / Play
  // Billing localized `priceString` from RevenueCat over the backend
  // env-based fallback. This is what Apple 3.1.2 expects: the displayed
  // price MUST match the App Store storefront exactly, per user's
  // Apple-ID region. Falls back to `priceDisplay` when RC isn't
  // configured (e.g. dev build without the API key).
  const [nativePriceString, setNativePriceString] = useState(null);
  const [nativePeriod, setNativePeriod] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!isNative()) return () => { cancelled = true; };
    (async () => {
      try {
        const rc = await import("../lib/revenueCat");
        // Iter XX.267d — single, canonical resolver. Waits (bounded)
        // for RC to configure, then walks cache → offering →
        // StoreKit-direct and returns the first valid priceString.
        // The moment ANY step wins we flip priceLoaded=true — we do
        // NOT wait for the offering path once the direct path has
        // already resolved a valid native price.
        const resolved = await rc.resolveDisplayPrice("plus_monthly_ios");
        if (cancelled) return;
        try {
          // eslint-disable-next-line no-console
          console.log("[XX.267d-price] Upgrade.jsx resolveDisplayPrice", {
            has_price:    !!resolved?.priceString,
            source:       resolved?.source ?? null,
            price_string: resolved?.priceString ?? null,
            currency:     resolved?.currencyCode ?? null,
          });
        } catch { /* diagnostic must never throw */ }
        if (resolved?.priceString) {
          setNativePriceString(resolved.priceString);
          // Apple's storefront also renders monthly context alongside
          // the priceString; we surface "/month" for UX consistency.
          setNativePeriod("month");
        }
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const effectivePriceDisplay = nativePriceString || priceDisplay;
  const effectivePricePeriod = nativePeriod || pricePeriod;
  // Iter XX.267a — `priceLoaded` reflects EITHER source. Declared here
  // (after nativePriceString state) so the boolean tracks the RC
  // resolution too, not just Stripe.
  const priceLoaded = !!local?.display || !!nativePriceString;

  // Iter XX.72.5 / XX.72.6 — Auto-resume checkout after identity capture.
  // Anonymous /upgrade → /chat?upgrade=1 → identity linked → we land
  // back here with ?autostart=1. Wait for pricing to load, then fire
  // startCheckout ONCE. Guarded by a ref so React.StrictMode doesn't
  // double-fire. Also clears the beginPlusPurchase in-flight guard —
  // that guard was set true on the outbound identity redirect and
  // persists via the module import; a page navigation loses the
  // JS context so it's effectively reset, but being explicit is safer.
  const autostartRef = useRef(false);
  useEffect(() => {
    if (!priceLoaded) return;
    if (!params.get("autostart")) return;
    if (autostartRef.current) return;
    let hasToken = false;
    try { hasToken = _hasToken(); } catch { /* noop */ }
    if (!hasToken) return; // still anonymous → don't loop
    autostartRef.current = true;
    resetPurchaseInFlight();
    // Small delay so the redirect banner / analytics fire first.
    const t = setTimeout(() => { startCheckout(); }, 100);
    return () => clearTimeout(t);
     
  }, [priceLoaded]);

  // Iter XX.168 — Fetch personalized Plus context. Read-only,
  // fire-and-forget. Anonymous / thin-history users get level=C
  // and the block silently hides.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/plus/personalization");
        if (alive) setPersonalization(data || null);
      } catch {
        if (alive) setPersonalization(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  const detectedCountry = local?.country || local?.detected_country || null;
  const freeLimit = pricing?.free_video_reviews ?? 3;
  const isFounder = billing?.is_founder;
  const isAlreadyPlus = billing?.plus_status === "active";
  const isCanceled = billing?.plus_status === "canceled";
  const plusEndsAt = billing?.plus_ends_at;

  async function startCheckout() {
    if (busy) return;
    // Iter XX.267b — light haptic the moment the CTA registers. Fired
    // BEFORE beginPlusPurchase so it lands even though the very next
    // line hard-navigates to Stripe / opens the native purchase sheet.
    hapticLight();
    // Iter XX.72.6 — canonical purchase flow. beginPlusPurchase()
    // handles: anonymous → identity → return-then-checkout, StrictMode
    // double-fire, 401 fallback, full funnel analytics, and toasts.
    // The `entryPoint` we hand it becomes the `source` on the Stripe
    // Checkout metadata + every audit event in the funnel.
    const cachedEntry = consumePlusPurchaseEntry();
    const entryPoint = cachedEntry?.entryPoint
      || (cameFromPaywall ? "paywall" : "plus_page");
    // Iter XX.79 · Clarity — fire BEFORE beginPlusPurchase because
    // the very next line hard-navigates to Stripe. If we fired after
    // the await, the tag would never dispatch.
    try {
      const { track } = require("../lib/clarity");
      track("checkout_started", { entry_point: entryPoint });
    } catch { /* clarity is best-effort */ }
    // Iter XX.267 — additive `plus_cta_clicked` mirror so we can
    // measure Plus CTA rate uniformly across all pricing surfaces.
    trackProEvent("plus_cta_clicked", entryPoint, gullySessionId);
    setBusy(true);
    try {
      await beginPlusPurchase({
        entryPoint,
        priceLoaded,
        country: detectedCountry || undefined,
      });
    } finally {
      // We only reach here if beginPlusPurchase failed early (no redirect).
      // On any redirect path, the page is already navigating away.
      setBusy(false);
    }
  }

  async function cancelSubscription() {
    if (busy) return;
    if (!window.confirm("Cancel Gully Plus? You'll keep access until your current period ends.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(`Cancellation scheduled. Plus access until ${data.plus_ends_at ? new Date(data.plus_ends_at).toLocaleDateString() : "period end"}.`);
      const refreshed = await api.get("/billing/me");
      setBilling(refreshed.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grain min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Iter XX.31 — trial banner on the upgrade page, tagged with
            `upgrade_page` source. Self-gated: eligible users see it,
            existing Plus / expired-trial users never do. */}
        <TrialBanner
          trialSource={cameFromPaywall ? "free_limit" : "upgrade_page"}
          onActivated={() => {
            try { window.dispatchEvent(new CustomEvent("gully:plus-changed")); }
            catch { /* noop */ }
            toast.success("Gully Plus is on for the next 30 days.");
          }}
        />

        {/* Iter XX.168 — Personalized Plus context. Renders only
            when the user has genuine history (level A or B). Level
            C silently hides so the generic Plus copy below owns
            the page. Zero fabrication: every slot is grounded in
            persisted data. */}
        <PersonalizedPlusBlock ctx={personalization} />

        {cameFromPaywall && (
          <p
            className="label-eyebrow mb-2"
            style={{ color: "var(--highlight)" }}
            data-testid="plus-paywall-eyebrow"
          >
            you&apos;ve used your {freeLimit} free video reviews
          </p>
        )}

        {/* Iter XX.50 — narrative header. One story, four beats:
            problem → why cricketers don't improve → how Gully solves it →
            why Plus exists. Existing sections below become supporting
            evidence, in this order:
              1. The transformation (headline block + value cards)
              2. Free vs Plus (comparison)
              3. Testimonials / proof (PlusSocialProof)
              4. Who it's for
              5. Pricing + CTA
            No feature-level content was removed — only re-ordered. */}
        <div className="mb-14" data-testid="plus-narrative">
          <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}
             data-testid="plus-narrative-eyebrow">
            Keep working on your cricket, week after week.
          </p>
          <h1
            className="font-display font-light text-4xl sm:text-5xl tracking-tight mb-6"
            data-testid="plus-narrative-headline"
          >
            Most cricketers don&apos;t improve<br />
            <span className="text-[#A7F3D0]">because they forget.</span>
          </h1>
          <div className="space-y-5 text-neutral-300 leading-relaxed max-w-xl text-lg"
               data-testid="plus-narrative-body">
            <p>
              You play a match. You spot the mistake. Two weeks later,
              you make it again. The cycle repeats.
            </p>
            <p>
              Gully changes this. Every conversation, every video review,
              every conversation builds a picture of your cricket — patterns,
              confidence, and the exact moments that shape your season.
            </p>
            <p className="text-neutral-100">
              <strong>Gully Plus</strong> is that intelligence system.
              It remembers what you&apos;ve learned, tracks how you&apos;re
              improving, and helps you avoid the same mistake twice.
            </p>
          </div>
        </div>

        {/* Transformation cards — kept, but retitled so they read as
            outcomes (not features). PlusValueCards already ships with
            outcome-first copy from /api/plus/value-preview. */}
        <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}
           data-testid="plus-transformation-eyebrow">
          The transformation
        </p>

        {/* Iter XX.17 — 6 benefit cards. `data` is populated with the
            player's real journey_snapshot substrate when available;
            null → generic copy only. */}
        <PlusValueCards preview={valuePreview} isAlreadyPlus={billing?.plus_status === "active"} />

        {/* Free vs Plus — the two-line distinction, top of the fold below
            the transformation. Iter XX.50 — moved above social proof
            and the "for players who…" band so the reader hits the
            plan clarity before scanning testimonials. */}
        <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}
           data-testid="plus-freevsplus-eyebrow">
          Free vs Gully Plus
        </p>
        <p className="text-neutral-400 text-sm mb-4 max-w-xl leading-relaxed"
           data-testid="plus-freevsplus-summary">
          <span className="text-neutral-100">Free</span> helps you after today&apos;s cricket.
          <span className="text-neutral-100 ml-2">Gully Plus</span> helps you improve
          across your entire cricket journey.
        </p>

        {/* Iter XX.17 — Mid-page CTA band. Iter XX.50 — moved BELOW
            the free vs plus comparison so the reader compares plans
            first, then decides. */}
        {/* placeholder-moved */}

        {/* Iter 70 — "for players who…" qualifier band. Iter XX.50 —
            moved below the comparison. */}
        {/* placeholder-moved */}

        {/* Iter 70.1 — Authentic social proof. Iter XX.50 — moved below
            comparison so plan clarity precedes testimonials. */}
        {/* placeholder-moved */}

        {/* Comparison */}
        <div
          className="grid md:grid-cols-2 gap-px mb-10"
          style={{ backgroundColor: "var(--border-default)" }}
          data-testid="plus-comparison"
        >
          {/* FREE */}
          <div className="surface-1 p-8" data-testid="plus-comparison-free">
            <p className="label-eyebrow mb-3">free · experience gully</p>
            <p className="font-display text-3xl mb-6">$0</p>
            <p className="text-sm text-neutral-400 mb-4 leading-relaxed">
              Free lets you experience Gully.
            </p>
            <ul className="space-y-3 text-neutral-300 leading-relaxed text-sm">
              <li>· Chat with Gully</li>
              <li>· Basic match &amp; practice review</li>
              <li>· {freeLimit} video reviews</li>
              <li>· Basic guidance</li>
            </ul>
          </div>

          {/* PLUS */}
          <div
            className="surface-1 p-8 relative"
            style={{ borderColor: "var(--highlight)", borderWidth: 1, borderStyle: "solid" }}
            data-testid="plus-comparison-plus"
          >
            {/* Iter XX.267 — MOST POPULAR badge anchors Plus as the
                recommended tier now that Pro ($99, Coming Soon) is
                positioned below. Does NOT change purchase flow. */}
            {!isAlreadyPlus && !isFounder && (
              <span
                data-testid="plus-most-popular-badge"
                className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white shadow-sm"
              >
                Most Popular
              </span>
            )}
            <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}>
              gully plus · committed to improving
            </p>
            <p className="font-display text-3xl mb-1" data-testid="plus-price-display">
              {effectivePriceDisplay}
              <span className="text-neutral-500 text-lg"> / {effectivePricePeriod}</span>
            </p>
            <p className="text-xs text-neutral-600 mb-4" data-testid="plus-price-currency">
              {priceLoaded ? `${priceCurrency} · billed ${pricePeriod}ly · charged exactly as shown` : "Loading localized price…"}
            </p>
            <p className="text-sm text-neutral-400 mb-4 leading-relaxed">
              Plus is for cricketers committed to improving throughout the season.
            </p>
            {/* Iter XX.275 — outcome-first Plus benefits. Founder-brief
                rewrite: lead with the cricket outcome, not the feature.
                Priority-replies benefit removed (no shipped service
                obligation behind it — see XX.275 audit). */}
            <ul
              className="space-y-3 text-neutral-200 leading-relaxed text-sm mb-8"
              data-testid="plus-benefits"
            >
              <li>· Keep one improvement focus active between sessions</li>
              <li>· Come back and continue instead of starting from zero</li>
              <li>· See recurring problems Gully has noticed across your matches</li>
              <li>· Every video review builds on the last</li>
              <li>· Track whether something is actually changing over time</li>
              <li>· <span className="text-[#A7F3D0]">Unlimited</span> video reviews</li>
            </ul>
            {isFounder ? (
              <div className="text-xs text-emerald-400" data-testid="plus-founder-state">
                Founder account — unlimited everything, no charge.
              </div>
            ) : isAlreadyPlus ? (
              <div data-testid="plus-already-plus">
                <p className="text-sm text-emerald-400 mb-1">You&apos;re on Gully Plus 🎉</p>
                {plusEndsAt && (
                  <p className="text-xs text-neutral-500 mb-4">
                    Renews on {new Date(plusEndsAt).toLocaleDateString()}
                  </p>
                )}
                <button
                  onClick={cancelSubscription}
                  disabled={busy}
                  className="btn-ghost text-xs disabled:opacity-60"
                  data-testid="plus-cancel-button"
                >
                  {busy ? "…" : "Cancel subscription"}
                </button>
              </div>
            ) : isCanceled ? (
              <div data-testid="plus-canceled-state">
                <p className="text-sm text-amber-400 mb-1">Cancellation scheduled</p>
                {plusEndsAt && (
                  <p className="text-xs text-neutral-500 mb-4">
                    You have Plus access until {new Date(plusEndsAt).toLocaleDateString()}.
                  </p>
                )}
                <button
                  data-testid="plus-resume-button"
                  disabled={busy}
                  onClick={startCheckout}
                  className="btn-primary w-full text-sm disabled:opacity-60"
                >
                  {busy ? "…" : "Resume subscription"}
                </button>
              </div>
            ) : (
              <>
                {/* Iter 70.3 — subtle mission line directly above CTA.
                    Existing typography, no banner, no highlight box,
                    no animation. The heart emoji is intentional — it's
                    the founder's voice, not decoration. */}
                <p
                  className="text-xs text-neutral-400 leading-relaxed mb-3"
                  data-testid="plus-mission-line"
                >
                  Every Gully Plus subscription helps me keep building better tools
                  for cricketers. <span aria-hidden>❤️</span>
                </p>
                {/* Iter XX.275 — soft coaching anchor. Positions Plus as
                    a between-sessions companion, NOT a coach replacement,
                    and makes no universal savings claim. */}
                <p
                  className="text-xs text-neutral-500 leading-relaxed mb-3"
                  data-testid="plus-coaching-anchor"
                >
                  Private coaching happens session by session. Gully Plus stays
                  with your cricket between sessions.
                </p>
                <button
                  data-testid="plus-checkout-button"
                  disabled={busy || !priceLoaded}
                  onClick={startCheckout}
                  className="btn-primary w-full text-sm disabled:opacity-60"
                >
                  {busy ? "Opening checkout…" : (priceLoaded ? "Keep working on my cricket" : "Loading price…")}
                </button>
                {priceLoaded && (
                  <p
                    className="text-center text-xs text-neutral-500 mt-2"
                    data-testid="plus-cta-price-line"
                  >
                    {effectivePriceDisplay} / {effectivePricePeriod} · cancel anytime
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Iter XX.267 — Gully Pro pricing anchor. Non-purchasable
            "Coming Soon" tier at $99/month with waitlist CTA. Sits
            BELOW the Free/Plus comparison so Plus remains the
            visually strongest purchasable card. Never modifies
            RevenueCat / Stripe / IAP / entitlements. */}
        <div className="mb-10" data-testid="pro-tier-section">
          <p
            className="label-eyebrow mb-3"
            style={{ color: "var(--highlight)" }}
            data-testid="pro-tier-eyebrow-heading"
          >
            Coming soon · Gully Pro
          </p>
          <ProTierCard
            source={cameFromPaywall ? "paywall_entry_upgrade_page" : "upgrade_page"}
            sessionId={gullySessionId}
          />
        </div>

        {/* Iter XX.50 — moved below the comparison. Proof → who it's for
            → final CTA band. Same original components + same analytics. */}

        {/* Testimonials / proof */}
        <PlusSocialProof />

        {/* Who Plus is for — merged into a tight qualifier band. */}
        <div className="surface-1 p-6 mb-10" data-testid="plus-who-its-for">
          <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}>
            Gully Plus is for cricketers who…
          </p>
          <ul className="space-y-2 text-neutral-300 leading-relaxed text-sm">
            <li>· Train consistently</li>
            <li>· Play matches regularly</li>
            <li>· Want long-term improvement</li>
            <li>· Want every session reviewed</li>
          </ul>
        </div>

        {/* Final CTA band — one strong close instead of a mid-page + end
            duplicate. Same startCheckout hook so analytics stay 1:1.
            Iter XX.275 — outcome-first headline + sub. */}
        {(!billing || billing?.plan !== "plus") && (
          <div
            className="surface-1 p-8 mb-6 text-center"
            data-testid="plus-value-cta-band"
          >
            <h2 className="font-display text-2xl mb-2" data-testid="plus-value-cta-headline">
              Keep working on your cricket.
            </h2>
            <p className="text-neutral-300 mb-6" data-testid="plus-value-cta-sub">
              Plus stays with your cricket between sessions — so improvement compounds.
            </p>
            {/* Iter XX.295 — Native IAP integrity gate: a build that cannot
                configure RevenueCat shows a truthful update-app state
                instead of this purchase button. Web unchanged. */}
            <NativePurchaseCta testId="plus-value-cta-unavailable">
            <button
              data-testid="plus-value-cta-button"
              disabled={busy || !priceLoaded}
              onClick={startCheckout}
              className="btn-primary text-sm disabled:opacity-60"
            >
              {busy ? "Opening checkout…" : `Upgrade to Gully Plus${priceLoaded ? ` · ${effectivePriceDisplay}/${effectivePricePeriod}` : ""}`}
            </button>
            </NativePurchaseCta>
          </div>
        )}

        <p className="text-xs text-neutral-600 mb-2" data-testid="plus-fineprint">
          {isIOS()
            ? "Subscription billed monthly through the App Store · cancel anytime in Settings → Apple ID → Subscriptions"
            : isNative()
              ? "Subscription billed monthly through Google Play · cancel anytime in Play Store → Subscriptions"
              : "Secure checkout by Stripe · recurring monthly · cancel anytime"}
        </p>

        {/* Iter XX.218 — App Review readiness. On native (iOS + Android)
            Restore Purchases must be visible directly on the Plus screen
            per Apple guideline 3.1.1 + Play policy. On web, Stripe restores
            automatically via billing email — the button is a no-op. */}
        {isNative() && (
          <div className="mt-3 mb-3" data-testid="plus-restore-block">
            <RestorePurchasesButton
              className="text-xs text-neutral-400 hover:text-neutral-200 underline decoration-neutral-700 underline-offset-2"
              label="Restore Purchases"
            />
          </div>
        )}

        {/* Iter XX.218 — Terms + Privacy must be visible/clickable near the
            subscription CTA (Apple 3.1.2). Uses the canonical URLs — no
            duplicate policy pages. Opens in new tab on web, in-webview on
            native. */}
        <p className="text-[11px] text-neutral-500 mb-3" data-testid="plus-legal-links">
          <a
            href="https://askgully.com/terms"
            target={isNative() ? "_self" : "_blank"}
            rel="noopener noreferrer"
            data-testid="plus-terms-link"
            className="underline decoration-neutral-700 underline-offset-2 hover:text-neutral-300"
          >
            Terms of Use
          </a>
          <span aria-hidden> · </span>
          <a
            href="https://askgully.com/privacy"
            target={isNative() ? "_self" : "_blank"}
            rel="noopener noreferrer"
            data-testid="plus-privacy-link"
            className="underline decoration-neutral-700 underline-offset-2 hover:text-neutral-300"
          >
            Privacy Policy
          </a>
        </p>

        {/* Iter XX.218 — In-app account deletion path (Apple 5.1.1(v)).
            Opens the existing authenticated /account/delete surface — no
            second deletion system built. */}
        <p className="text-[11px] text-neutral-500 mb-3" data-testid="plus-account-links">
          <Link
            to="/account/delete"
            data-testid="plus-delete-account-link"
            className="underline decoration-neutral-700 underline-offset-2 hover:text-neutral-300"
          >
            Account &amp; Privacy → Delete my account
          </Link>
        </p>
        <Link
          to="/chat"
          className="btn-ghost text-sm"
          data-testid="plus-back-link"
        >
          ← Back to chat
        </Link>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter XX.17 — Gully Plus value cards.
// Renders the 6 benefit cards returned by /api/plus/value-preview. Each card
// shows its icon + title + generic copy PLUS a "your data" line when the
// backend populated `card.data` (only happens for signed-in users who have
// a `player_memory.journey_snapshot`).
// ---------------------------------------------------------------------------
function PlusValueCards({ preview, isAlreadyPlus }) {
  const cards = preview?.cards;
  if (!cards || cards.length === 0) {
    // Loading — reserve vertical space so the layout doesn't shift.
    return (
      <div
        className="mb-10 h-40 rounded-lg border border-neutral-900 bg-neutral-950/30"
        data-testid="plus-value-cards-loading"
        aria-hidden
      />
    );
  }
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10"
      data-testid="plus-value-cards"
    >
      {cards.map((c) => (
        <ValueCard key={c.id} card={c} />
      ))}
      {isAlreadyPlus && (
        <div
          className="md:col-span-2 text-center text-xs text-emerald-400 mt-1"
          data-testid="plus-value-already-plus"
        >
          You&apos;re on Gully Plus ✓ — this is everything you already have.
        </div>
      )}
    </div>
  );
}

function ValueCard({ card }) {
  const data = card?.data;
  return (
    <div
      className="surface-1 p-5 flex gap-4"
      data-testid={`plus-value-card-${card.id}`}
    >
      <span aria-hidden className="text-2xl leading-none select-none pt-0.5">
        {card.icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-100 mb-1">{card.title}</p>
        <p className="text-sm text-neutral-400 leading-relaxed">{card.copy}</p>
        {data && (
          <div
            className="mt-3 pt-3 border-t border-neutral-800 space-y-1"
            data-testid={`plus-value-card-${card.id}-data`}
          >
            {renderCardData(card.id, data)}
          </div>
        )}
      </div>
    </div>
  );
}

function renderCardData(id, data) {
  const rows = [];
  if (id === "cricket_journey" && data.matches != null) {
    rows.push(["Matches", data.matches]);
  } else if (id === "long_term_memory") {
    if (data.strength) rows.push(["Strength", data.strength]);
    if (data.recurring_pattern) rows.push(["Recurring pattern", data.recurring_pattern]);
  } else if (id === "confidence_tracking" && data.confidence_trend) {
    rows.push(["Confidence trend", data.confidence_trend]);
  } else if (id === "season_reviews") {
    if (data.latest_review_headline) rows.push(["Latest review", data.latest_review_headline]);
    if (data.next_review_at != null) rows.push(["Next review", `${data.matches} / ${data.next_review_at} matches`]);
  } else if (id === "improvement_tracking" && data.biggest_improvement) {
    rows.push(["Biggest improvement", data.biggest_improvement]);
  } else if (id === "personalized_coaching" && data.identity_arc) {
    rows.push(["", data.identity_arc]);
  }
  if (rows.length === 0) return null;
  return rows.map(([label, value], i) => (
    <div key={i} className="text-xs">
      {label && (
        <span className="text-[10px] uppercase tracking-wider text-neutral-500 mr-2">
          {label}:
        </span>
      )}
      <span className="text-neutral-200">{value}</span>
    </div>
  ));
}



// ---------------------------------------------------------------------------
// Iter 70.1 — Social proof section.
// Values come from `SOCIAL_PROOF` constants (founder-editable). Any `null`
// metric or empty countries list is silently omitted — no placeholders. If
// EVERY metric is unavailable, the whole section renders nothing so we
// never show an empty band.
// ---------------------------------------------------------------------------
function _formatCount(n) {
  // Store raw floor (e.g. 700, 4000). Render as "700+" / "4,000+" so the
  // number is precise-looking but visibly rounded.
  if (typeof n !== "number" || !isFinite(n) || n <= 0) return null;
  const s = Math.floor(n).toLocaleString("en-US");
  return `${s}+`;
}

function PlusSocialProof() {
  // Iter 70.2 — live metrics from /api/plus/social-proof (cheap,
  // 5-min cached backend endpoint that reuses existing collections —
  // no new writes, no new indexes). Falls back to the static
  // `SOCIAL_PROOF` constants if the endpoint is unreachable, so the
  // band still renders during a backend blip.
  const [live, setLive] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/plus/social-proof")
      .then((r) => { if (!cancelled) setLive(r?.data || null); })
      .catch(() => { /* silent — constants fallback below */ });
    return () => { cancelled = true; };
  }, []);

  const src = live || {
    total_players:      SOCIAL_PROOF.totalPlayers,
    total_reviews:      SOCIAL_PROOF.totalReviews,
    total_conversations: SOCIAL_PROOF.totalMessages,
    top_countries:      SOCIAL_PROOF.countries,
  };

  const players  = _formatCount(src.total_players);
  const reviews  = _formatCount(src.total_reviews);
  const messages = _formatCount(src.total_conversations);
  const countryList = Array.isArray(src.top_countries) && src.top_countries.length > 0
    ? src.top_countries
    : null;
  // Iter 70.3 — "Used by cricketers from …" wording; graceful fallback
  // to "around the world" so the row still renders when country data
  // is unavailable (never a truthy-empty visual).
  const geoLabel = countryList
    ? `Used by cricketers from ${countryList.join(", ")} and more.`
    : "Used by cricketers around the world.";

  const rows = [
    // Iter 70.3 — "cricket coaching sessions" is the accurate label:
    // `total_players` is `web_messages.distinct(\"session_id\")`.length,
    // i.e. one chat SESSION per distinct id — not necessarily one unique
    // human. Truthful framing beats a bigger number.
    players  && { key: "players",   icon: "\u{1F3CF}", label: `${players} cricket coaching sessions` },
    reviews  && { key: "reviews",   icon: "\u{1F3A5}", label: `${reviews} video reviews completed` },
    messages && { key: "messages",  icon: "\u{1F4AC}", label: `${messages} cricket conversations` },
    // Country row always renders (either real list or "around the world")
    // so we never drop below three lines of proof.
    { key: "countries", icon: "\u{1F30D}", label: geoLabel },
  ].filter(Boolean);

  // If EVERY count-metric is unavailable AND we're still on the fallback
  // "around the world" line, the band would carry only one row — that
  // reads weak. Hide the whole section in that edge case.
  const hasAnyCount = !!(players || reviews || messages);
  if (!hasAnyCount && !countryList) return null;

  return (
    <div className="surface-1 p-6 mb-10" data-testid="plus-social-proof">
      <p className="label-eyebrow mb-3" style={{ color: "var(--highlight)" }}>
        trusted by serious cricketers around the world.
      </p>
      <ul className="space-y-2 text-neutral-300 leading-relaxed text-sm">
        {rows.map((r) => (
          <li key={r.key} className="flex items-start gap-2" data-testid={`plus-social-proof-${r.key}`}>
            <span aria-hidden className="select-none">{r.icon}</span>
            <span>{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ============================================================
// Iter XX.168 — Personalized Plus block.
//
// Fixed structure + dynamic slots (never full LLM generation).
// Every slot renders only when its data field is truthy — thin-
// history users see nothing and the generic Plus copy below owns
// the page. No fabrication.
//
// Level A — hero + goal/pattern/focus + continuity + journey
// Level B — hero (if grounded) + one or two slots + continuity
// Level C — nothing (returns null)
// ============================================================
function PersonalizedPlusBlock({ ctx }) {
  if (!ctx || ctx.level === "C") return null;
  const slots = ctx.slots || {};
  const counts = ctx.counts || {};
  const {
    primary_goal, role, current_challenge, observed_pattern,
    current_focus, recent_evidence, journey_summary,
  } = slots;

  // Rich-history counts we can honestly cite.
  const sessionCount = Number(counts.sessions || 0);
  const videoCount = Number(counts.video_reviews || 0);
  const hasJourney = Boolean(journey_summary);

  // Continuity block always renders when the block itself is
  // rendering — it's the differentiator, and it does not require
  // per-slot personalization.
  const heroLine = ctx.hero_line || null;

  return (
    <section
      className="mb-10 space-y-5"
      data-testid="plus-personalized-block"
      data-level={ctx.level}
    >
      {/* Hero — replaces the generic opener when we have grounded
          context. */}
      {heroLine ? (
        <div data-testid="plus-personalized-hero">
          <p
            className="label-eyebrow mb-2"
            style={{ color: "var(--highlight)" }}
          >
            your gully plus
          </p>
          <h2 className="text-2xl sm:text-3xl font-semibold leading-tight text-neutral-100">
            {heroLine}
          </h2>
          <p className="text-sm text-neutral-400 mt-2">
            Here&apos;s what Gully has started learning about your
            cricket — and what we&apos;d keep working on together.
          </p>
        </div>
      ) : null}

      {/* What Gully Already Knows — only shows populated rows. */}
      {(primary_goal || current_challenge || observed_pattern ||
        current_focus) ? (
        <div
          className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-5"
          data-testid="plus-what-gully-knows"
        >
          <p className="label-eyebrow mb-3 text-neutral-400">
            what gully already knows
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {primary_goal ? (
              <div data-testid="plus-slot-goal">
                <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
                  your goal
                </dt>
                <dd className="text-sm text-neutral-100 mt-1">{primary_goal}</dd>
              </div>
            ) : null}
            {observed_pattern ? (
              <div data-testid="plus-slot-pattern">
                <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
                  pattern we&apos;re watching
                </dt>
                <dd className="text-sm text-neutral-100 mt-1">{observed_pattern}</dd>
              </div>
            ) : null}
            {current_focus ? (
              <div data-testid="plus-slot-focus">
                <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
                  current focus
                </dt>
                <dd className="text-sm text-neutral-100 mt-1">{current_focus}</dd>
              </div>
            ) : null}
            {current_challenge && current_challenge !== current_focus ? (
              <div data-testid="plus-slot-challenge">
                <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
                  what we&apos;re working on
                </dt>
                <dd className="text-sm text-neutral-100 mt-1">{current_challenge}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      ) : null}

      {/* Continuity differentiator — the "your cricket doesn't
          reset after this chat" narrative. Renders whenever the
          block is rendering. */}
      <div
        className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-5"
        data-testid="plus-continuity-block"
      >
        <p className="label-eyebrow mb-3 text-neutral-400">
          your cricket doesn&apos;t reset after this chat
        </p>
        <p className="text-sm text-neutral-300 leading-relaxed">
          Today&apos;s conversation gives Gully one piece of the
          picture. Come back after your next match or training
          session and Gully can compare what happened, see whether
          the same pattern returned, update what you&apos;re working
          on, and keep building your cricket journey.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="rounded border border-neutral-800 bg-neutral-900/60 p-3">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">
              today
            </p>
            <p className="text-neutral-200 mt-1">
              {observed_pattern
                ? `We noticed: ${observed_pattern}`
                : (current_focus
                    ? `We noticed: ${current_focus}`
                    : "We started building your context.")}
            </p>
          </div>
          <div className="rounded border border-neutral-800 bg-neutral-900/60 p-3">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">
              next session
            </p>
            <p className="text-neutral-200 mt-1">
              {current_focus
                ? `We'll watch: ${current_focus}`
                : "We'll watch whether the same pattern returns."}
            </p>
          </div>
          <div className="rounded border border-neutral-800 bg-neutral-900/60 p-3">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">
              over time
            </p>
            <p className="text-neutral-200 mt-1">
              Gully builds: patterns → progress → what to work on next.
            </p>
          </div>
        </div>
      </div>

      {/* Journey citation — only when we can honestly cite counts. */}
      {hasJourney && (sessionCount > 0 || videoCount > 0) ? (
        <p
          className="text-xs text-neutral-500"
          data-testid="plus-journey-citation"
        >
          You&apos;ve given Gully{" "}
          {sessionCount > 0
            ? `${sessionCount} coaching session${sessionCount === 1 ? "" : "s"}`
            : ""}
          {sessionCount > 0 && videoCount > 0 ? " and " : ""}
          {videoCount > 0
            ? `${videoCount} video review${videoCount === 1 ? "" : "s"}`
            : ""}{" "}
          to learn from. The more of your season Gully sees, the
          clearer your recurring patterns and improvements can become.
        </p>
      ) : null}

      {/* Personalized recent evidence — only when we have a real
          completed Outcome to cite. Uses the Outcome's reason as
          the anchor; never quotes back player text. */}
      {recent_evidence && recent_evidence.reason ? (
        <div
          className="rounded-lg border border-emerald-900/50 bg-emerald-950/25 p-5"
          data-testid="plus-recent-outcome"
        >
          <p className="label-eyebrow mb-2 text-emerald-300/80">
            today we found something worth building on
          </p>
          <p className="text-sm text-emerald-100 leading-relaxed">
            The valuable part now is finding out whether it happens
            again. With Plus, your next match builds on this one
            instead of starting another conversation from scratch.
          </p>
        </div>
      ) : null}
    </section>
  );
}

